import { useCallback, useEffect, useState } from 'react';
import {
  finintelApi,
  type BusinessHealthRecord,
  type CashForecastRecord,
  type FinancialDashboard,
  type FinancialRecommendation,
  type FinancialRiskRecord,
  type ProfitabilityRecord,
  type ScenarioRecord,
  type WorkingCapitalRecord,
} from '../../api/finintel';

type Tab = 'overview' | 'cashflow' | 'scenarios' | 'health' | 'profit' | 'recommendations' | 'risks';

function severityClass(s?: string) {
  if (s === 'high' || s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 65) return 'text-amber-700';
  return 'text-red-700';
}

function fmtCurrency(n: number) {
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function impactClass(n: number) {
  if (n > 0) return 'text-emerald-700';
  if (n < 0) return 'text-red-700';
  return 'text-slate-600';
}

export function FinIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<FinancialDashboard | null>(null);
  const [cashFlow, setCashFlow] = useState<CashForecastRecord[]>([]);
  const [workingCapital, setWorkingCapital] = useState<WorkingCapitalRecord | null>(null);
  const [profitability, setProfitability] = useState<ProfitabilityRecord[]>([]);
  const [health, setHealth] = useState<BusinessHealthRecord | null>(null);
  const [recommendations, setRecommendations] = useState<FinancialRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioRecord[]>([]);
  const [risks, setRisks] = useState<FinancialRiskRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cf, wc, prof, hs, recs, sc, rsk] = await Promise.all([
        finintelApi.dashboard(),
        finintelApi.listCashFlow(),
        finintelApi.listWorkingCapital(),
        finintelApi.listProfitability(),
        finintelApi.listHealthScores(),
        finintelApi.listRecommendations(),
        finintelApi.listScenarios(),
        finintelApi.listRisks(),
      ]);
      setDashboard(dash);
      setCashFlow(cf);
      setWorkingCapital(wc[0] ?? null);
      setProfitability(prof);
      setHealth(hs[0] ?? null);
      setRecommendations(recs);
      setScenarios(sc);
      setRisks(rsk);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load financial intelligence data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await finintelApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const runScenario = async (type: string) => {
    setBusy(true);
    try {
      await finintelApi.runScenario({ type });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Financial Dashboard' },
    { id: 'cashflow', label: 'Cash Flow Planner' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'health', label: 'Business Health Console' },
    { id: 'profit', label: 'Profit Explorer' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
    { id: 'risks', label: 'Risk Center' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Financial Intelligence</h1>
          <p className="text-sm text-slate-500">
            Cash flow, working capital, profitability &amp; AI CFO — FRP-00005
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Cash Position', value: fmtCurrency(dashboard.cash_position) },
              { label: 'Cash Runway', value: `${dashboard.cash_runway_days} days` },
              { label: 'Net Working Capital', value: fmtCurrency(dashboard.net_working_capital) },
              { label: 'Financial Health', value: dashboard.financial_health_score.toFixed(1) },
              { label: 'Revenue (Brand)', value: fmtCurrency(dashboard.total_revenue) },
              { label: 'Gross Margin', value: `${dashboard.gross_margin_pct.toFixed(1)}%` },
              { label: 'Net Margin', value: `${dashboard.net_margin_pct.toFixed(1)}%` },
              { label: 'Liquidity Risk', value: dashboard.liquidity_risk ?? '—' },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          {workingCapital && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-medium text-slate-900">Working Capital Snapshot</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                <div><span className="text-slate-500">Inventory Investment:</span> {fmtCurrency(workingCapital.inventory_investment)}</div>
                <div><span className="text-slate-500">Carrying Cost:</span> {fmtCurrency(workingCapital.inventory_carrying_cost)}/mo</div>
                <div><span className="text-slate-500">Cash Conversion Cycle:</span> {workingCapital.cash_conversion_cycle_days} days</div>
                <div><span className="text-slate-500">Tax Obligations:</span> {fmtCurrency(workingCapital.tax_obligations)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'cashflow' && (
        <div className="space-y-4">
          {cashFlow.map((cf) => (
            <div key={cf.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium capitalize text-slate-900">{cf.period} Cash Forecast</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(cf.liquidity_risk === 'low' ? 'low' : 'medium')}`}>
                  {cf.liquidity_risk} liquidity
                </span>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5 text-sm">
                <div><p className="text-slate-500">Opening</p><p className="font-medium">{fmtCurrency(cf.opening_balance)}</p></div>
                <div><p className="text-slate-500">Inflows</p><p className="font-medium text-emerald-700">+{fmtCurrency(cf.inflows)}</p></div>
                <div><p className="text-slate-500">Outflows</p><p className="font-medium text-red-700">-{fmtCurrency(cf.outflows)}</p></div>
                <div><p className="text-slate-500">Closing</p><p className="font-medium">{fmtCurrency(cf.closing_balance)}</p></div>
                <div><p className="text-slate-500">Runway</p><p className="font-medium">{cf.cash_runway_days} days</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {['demand_increase', 'demand_decline', 'settlement_delay', 'advertising_increase'].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => runScenario(type)}
                disabled={busy}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Run {type.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
          {scenarios.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{s.title}</h3>
              {s.description && <p className="mt-1 text-sm text-slate-600">{s.description}</p>}
              <div className="mt-3 flex flex-wrap gap-6 text-sm">
                <span className={impactClass(s.cash_impact)}>Cash: {fmtCurrency(s.cash_impact)}</span>
                <span className={impactClass(s.profit_impact)}>Profit: {fmtCurrency(s.profit_impact)}</span>
                <span className={impactClass(s.revenue_impact)}>Revenue: {fmtCurrency(s.revenue_impact)}</span>
                <span className="text-slate-500">Liquidity: {s.liquidity_impact}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && health && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-medium text-slate-900">Business Health Scores</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Financial Health', score: health.financial_health_score },
              { label: 'Liquidity', score: health.liquidity_score },
              { label: 'Working Capital', score: health.working_capital_score },
              { label: 'Profitability', score: health.profitability_score },
              { label: 'Growth Sustainability', score: health.growth_sustainability_score },
              { label: 'Operational Efficiency', score: health.operational_efficiency_score },
            ].map((h) => (
              <div key={h.label} className="text-center">
                <p className="text-sm text-slate-500">{h.label}</p>
                <p className={`mt-1 text-3xl font-semibold ${healthClass(h.score)}`}>{h.score.toFixed(0)}</p>
                <div className="mx-auto mt-2 h-2 w-full max-w-[120px] overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${h.score >= 80 ? 'bg-emerald-500' : h.score >= 65 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${h.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'profit' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Dimension</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Revenue</th>
                <th className="px-4 py-2">Gross Profit</th>
                <th className="px-4 py-2">Net Profit</th>
                <th className="px-4 py-2">Gross Margin</th>
                <th className="px-4 py-2">Net Margin</th>
              </tr>
            </thead>
            <tbody>
              {profitability.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 capitalize">{p.dimension}</td>
                  <td className="px-4 py-2 font-medium">{p.dimension_value}</td>
                  <td className="px-4 py-2">{fmtCurrency(p.revenue)}</td>
                  <td className="px-4 py-2">{fmtCurrency(p.gross_profit)}</td>
                  <td className="px-4 py-2">{fmtCurrency(p.net_profit)}</td>
                  <td className="px-4 py-2">{p.gross_margin_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{p.net_margin_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  Priority #{r.priority_rank}
                </span>
              </div>
              {r.evidence && (
                <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  <span className="font-medium">Evidence:</span> {r.evidence}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                <span className={impactClass(r.cash_impact)}>Cash: {fmtCurrency(r.cash_impact)}</span>
                <span className={impactClass(r.profit_impact)}>Profit: {fmtCurrency(r.profit_impact)}</span>
                <span className="text-slate-500">Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.requires_approval && (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">Requires approval</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'risks' && (
        <div className="space-y-3">
          {risks.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium text-slate-900">{r.title}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(r.severity)}`}>
                  {r.severity}
                </span>
              </div>
              {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
              <p className="mt-1 text-xs text-slate-500">
                Cash at risk: {fmtCurrency(r.cash_at_risk)} · {r.category?.replace(/_/g, ' ')}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
